import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
  Container,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { User } from "features/Users/types/user.types";
import {
  createUser,
  updateUser,
  getUser,
} from "features/Users/services/UserService";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getRoleOptions } from "features/RolesAndPermissions/services/RoleService";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";
import userPlaceholder from "assets/images/users/user-dummy-img.jpg";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { updateAuthUser } from "features/Authentication/store/login/reducer";

const UserForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(!isEdit ? "New User" : "Edit User");

  const dispatch = useDispatch();
  const { isSuperAdmin, isYou } = useProfile();
  const canChangeRole = Boolean(isSuperAdmin);

  const validationSchema = useMemo(
    () =>
      schema.shape({
        roleIds: canChangeRole
          ? Yup.array()
              .of(Yup.number().required())
              .min(1, "At least one role is required")
              .required("The role field is required")
          : Yup.array().of(Yup.number()).nullable().optional(),
      }),
    [canChangeRole],
  );

  const [record, setRecord] = useState<User>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Only PNG, JPG, and JPEG images are accepted.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      validation.setFieldValue("avatar", file);
      setAvatarPreview(e.target.result as string);
      setRemoveAvatar(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    validation.setFieldValue("avatar", null);
    setAvatarPreview(null);
    setRemoveAvatar(true);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: record?.firstName ?? "",
      lastName: record?.lastName ?? "",
      email: record?.email ?? "",
      phone: record?.phone ?? "",
      password: "",
      confirmPassword: "",
      roleIds: record?.roles?.map((r) => r.id) ?? [],
      avatar: null as File | null,
      isEdit,
    },
    validationSchema,
    onSubmit: async (values) => {
      const roleIdsToSubmit =
        isEdit && !canChangeRole && (record?.roles?.length ?? 0) > 0
          ? record!.roles!.map((r) => r.id)
          : values.roleIds;

      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("phone", values.phone ?? "");
      (roleIdsToSubmit ?? []).forEach((rid: number, i: number) => {
        formData.append(`roleIds[${i}]`, String(rid));
      });

      if (values.avatar instanceof File) {
        formData.append("avatar", values.avatar);
      } else if (removeAvatar) {
        formData.append("removeAvatar", "true");
      }
      if (!isEdit) {
        formData.append("password", values.password);
        formData.append("confirmPassword", values.confirmPassword);
      }

      try {
        const response = isEdit
          ? await updateUser(Number(id), formData)
          : await createUser(formData);
        if (response.success) {
          toast.success(response.message);
          if (isEdit && isYou(Number(id))) {
            const updated = response.data as any;
            dispatch(
              updateAuthUser({
                firstName: values.firstName,
                lastName: values.lastName,
                ...(removeAvatar
                  ? { avatar: undefined }
                  : updated?.avatar !== undefined
                    ? { avatar: updated.avatar }
                    : {}),
              }),
            );
          }
          navigate(-1);
        }
      } catch (err) {
        const msg =
          (err as any)?.response?.data?.message ??
          (err as any)?.message ??
          "Something went wrong";
        toast.error(msg);
        console.warn(err);
      }
    },
  });

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id || isNaN(Number(id))) return;
      try {
        const response = await getUser(Number(id));
        const { success, message, data } = response;
        if (success) {
          setRecord(data);
        }
        console.log(message);
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit User" : "New User"}
            pageTitle="User Management"
            backTo={{ label: "Users", path: "/user-management/users" }}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row>
              <Col md={8}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title">User Details</h5>
                    <p className="card-subtitle text-muted">
                      Please fill in the user details
                    </p>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-5">
                      <div className="text-center">
                        <div className="position-relative d-inline-block">
                          <div className="position-absolute top-100 start-100 translate-middle">
                            <Label
                              htmlFor="avatar-input"
                              className="mb-0"
                              data-bs-toggle="tooltip"
                              data-bs-placement="right"
                              title="Select Image"
                            >
                              <div className="avatar-xs cursor-pointer">
                                <div className="avatar-title bg-light border rounded-circle text-muted">
                                  <i className="ri-image-fill"></i>
                                </div>
                              </div>
                            </Label>
                            <Input
                              innerRef={avatarInputRef}
                              className="form-control d-none"
                              id="avatar-input"
                              type="file"
                              accept=".jpg,.jpeg,.png"
                              onChange={handleImageChange}
                              invalid={
                                validation.touched.avatar &&
                                validation.errors.avatar
                                  ? true
                                  : false
                              }
                            />
                          </div>
                          <div className="avatar-lg">
                            <div
                              className={`avatar-title bg-light rounded ${
                                validation.touched.avatar &&
                                validation.errors.avatar
                                  ? "border border-danger"
                                  : ""
                              }`}
                            >
                              <img
                                src={
                                  removeAvatar
                                    ? userPlaceholder
                                    : (avatarPreview ??
                                      record?.avatar ??
                                      userPlaceholder)
                                }
                                alt="User Avatar"
                                className="avatar-md h-auto"
                              />
                            </div>
                          </div>
                        </div>
                        {validation.errors.avatar &&
                        validation.touched.avatar ? (
                          <FormFeedback type="invalid" className="d-block mt-3">
                            {" "}
                            {validation.errors.avatar}{" "}
                          </FormFeedback>
                        ) : null}
                        <small className="form-text text-muted d-block mt-2">
                          PNG, JPG, or JPEG only.
                        </small>
                        {(avatarPreview || record?.avatar) && !removeAvatar ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 mt-2"
                            onClick={handleRemoveAvatar}
                          >
                            <i className="ri-delete-bin-line me-1" />
                            Remove photo
                          </button>
                        ) : null}
                        <small className="form-text text-muted d-block mt-2">
                          Click the icon to upload a profile photo. Accepted
                          formats: PNG, JPG, or JPEG.
                        </small>
                      </div>
                    </div>
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label className="form-label">First Name</Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter first name"
                            name="firstName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.firstName || ""}
                            invalid={
                              validation.touched.firstName &&
                              validation.errors.firstName
                                ? true
                                : false
                            }
                          />
                          {validation.touched.firstName &&
                          validation.errors.firstName ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.firstName}</div>
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label className="form-label">Last Name</Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter last name"
                            name="lastName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.lastName || ""}
                            invalid={
                              validation.touched.lastName &&
                              validation.errors.lastName
                                ? true
                                : false
                            }
                          />
                          {validation.touched.lastName &&
                          validation.errors.lastName ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.lastName}</div>
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            type="email"
                            className="form-control"
                            placeholder="Enter email"
                            name="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.email}</div>
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label className="form-label">Phone</Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter phone"
                            name="phone"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.phone || ""}
                            invalid={
                              validation.touched.phone &&
                              validation.errors.phone
                                ? true
                                : false
                            }
                          />
                          {validation.touched.phone &&
                          validation.errors.phone ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.phone}</div>
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {!isEdit && (
                  <Card className="mt-3">
                    <CardHeader>
                      <h5 className="card-title mb-0">Account Security</h5>
                      <p className="card-subtitle text-muted mb-0 mt-1">
                        Set a password for the new user
                      </p>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label">Password</Label>
                            <InputGroup>
                              <Input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                placeholder="Enter Password"
                                name="password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.password || ""}
                                invalid={
                                  validation.touched.password &&
                                  validation.errors.password
                                    ? true
                                    : false
                                }
                              />
                              <InputGroupText>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-muted"
                                  onClick={() =>
                                    setShowPassword((prev) => !prev)
                                  }
                                  aria-label={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  <i
                                    className={
                                      showPassword
                                        ? "ri-eye-off-line fs-16"
                                        : "ri-eye-line fs-16"
                                    }
                                  />
                                </button>
                              </InputGroupText>
                            </InputGroup>
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid" className="d-block">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label">
                              Confirm Password
                            </Label>
                            <InputGroup>
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                className="form-control"
                                placeholder="Enter Confirm Password"
                                name="confirmPassword"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.confirmPassword || ""}
                                invalid={
                                  validation.touched.confirmPassword &&
                                  validation.errors.confirmPassword
                                    ? true
                                    : false
                                }
                              />
                              <InputGroupText>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-muted"
                                  onClick={() =>
                                    setShowConfirmPassword((prev) => !prev)
                                  }
                                  aria-label={
                                    showConfirmPassword
                                      ? "Hide confirm password"
                                      : "Show confirm password"
                                  }
                                >
                                  <i
                                    className={
                                      showConfirmPassword
                                        ? "ri-eye-off-line fs-16"
                                        : "ri-eye-line fs-16"
                                    }
                                  />
                                </button>
                              </InputGroupText>
                            </InputGroup>
                            {validation.touched.confirmPassword &&
                            validation.errors.confirmPassword ? (
                              <FormFeedback type="invalid" className="d-block">
                                {validation.errors.confirmPassword}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}
              </Col>

              <Col lg={4}>
                <Card>
                  <CardHeader>
                    <div className="d-flex align-items-center gap-2">
                      <span className="avatar-sm flex-shrink-0 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                        <i className="ri-shield-user-line fs-18" />
                      </span>
                      <div>
                        <h5 className="card-title mb-0">Assign Role</h5>
                        <p className="text-muted small mb-0 mt-1">
                          Controls what this user can access
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-0">
                      <Label className="form-label fw-medium">Role</Label>
                      <AsyncPaginatedSelect
                        isMulti
                        defaultOption={
                          record?.roles?.length
                            ? record.roles.map((r) => ({
                                value: r.id,
                                label: r.name ?? r.code,
                              }))
                            : undefined
                        }
                        fetchOptions={getRoleOptions}
                        placeholder="Choose roles..."
                        value={validation.values.roleIds}
                        onChange={(vals: any) =>
                          validation.setFieldValue(
                            "roleIds",
                            ((vals as any[]) ?? []).map((v: any) => v.value),
                          )
                        }
                        onBlur={() =>
                          validation.setFieldTouched("roleIds", true)
                        }
                        isDisabled={!canChangeRole}
                        isInvalid={
                          validation.touched.roleIds &&
                          !!validation.errors.roleIds
                        }
                      />
                      <small className="text-muted d-block mt-1">
                        {canChangeRole
                          ? "This determines what the user can see and do in the system."
                          : "Only a super administrator can assign or change this user’s role."}
                      </small>
                      {validation.touched.roleIds &&
                      validation.errors.roleIds ? (
                        <FormFeedback type="invalid" className="d-block mt-1">
                          {validation.errors.roleIds as string}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <hr className="my-3" />
                    <p className="text-muted small fw-medium mb-2">
                      System roles
                    </p>
                    <ul className="list-unstyled mb-0 vstack gap-2">
                      <li>
                        <span className="fw-medium text-danger small">
                          Super Admin
                        </span>
                        <p className="text-muted small mb-0">
                          Full unrestricted access to all modules, settings, and
                          user management.
                        </p>
                      </li>
                      <li>
                        <span className="fw-medium text-primary small">
                          Human Resource
                        </span>
                        <p className="text-muted small mb-0">
                          Manages employees, leaves, attendance, payroll, and HR
                          workflows.
                        </p>
                      </li>
                      <li>
                        <span className="fw-medium text-primary small">
                          Accounts
                        </span>
                        <p className="text-muted small mb-0">
                          Manages invoices, purchase orders, payments, and
                          financial records.
                        </p>
                      </li>
                      <li>
                        <span className="fw-medium text-primary small">
                          Marketing
                        </span>
                        <p className="text-muted small mb-0">
                          Creates and manages enquiries, quotations, petty
                          works, work orders, and AMCs. Assigns work to project
                          engineers.
                        </p>
                      </li>
                      <li>
                        <span className="fw-medium text-primary small">
                          Project Engineer
                        </span>
                        <p className="text-muted small mb-0">
                          Views assigned petty works, work orders, and AMCs. Can
                          start, edit, and complete site visits, visit logs,
                          petty works, and work orders.
                        </p>
                      </li>
                      <li>
                        <span className="fw-medium small">Employee</span>
                        <p className="text-muted small mb-0">
                          Basic access — view own profile, submit leaves, and
                          log attendance.
                        </p>
                      </li>
                    </ul>
                  </CardBody>
                </Card>
              </Col>
              <Col md={8}>
                <div className="mb-3 justify-content-end d-flex gap-3">
                  <BackButton />
                  <button
                    type="submit"
                    className="btn btn-primary w-sm"
                    disabled={validation.isSubmitting}
                  >
                    {validation.isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        {isEdit ? "Updating..." : "Submitting..."}
                      </>
                    ) : isEdit ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserForm;
